/**
 * useCatchballAcceptRight / usePriorityListRatifyRight — may the signed-in
 * person accept this proposal, or ratify this Priority List? (D-4, mig 987.)
 * useSessionPublishRight / usePriorityListApproveRight — may they publish this
 * session's results, or approve items onto this cycle's Priority List? (1001.)
 *
 * Each asks the database's `can_*` function once per id: today's rule, then the
 * cycle's "Items / selection" decision right when one is set — the answer
 * `catchball_accept`, `ratify_priority_list`, `publish_session_outcome` and
 * `approve_priority_list` act on, so the page and the act cannot disagree. A
 * page draws Accept or Ratify only when `allowed`, and shows `line` in its
 * place otherwise (build contract §2.8). While `loading`, draw neither.
 * Rejecting and returning a proposal exercise the same right as accepting it,
 * so those controls read the accept answer through `refusedByRight` (1001).
 *
 * Pass null to skip the call (a surface where the control would not be drawn
 * anyway).
 *
 * Approve is drawn only when `allowed` (1017): the database's own rule decides
 * it, so a page never draws it for a person the act would refuse.
 */

import { useEffect, useMemo, useState } from 'react';
import type { ActRight } from '@/lib/actRight';
import { fetchAcceptRight } from '@/lib/catchball/proposalDecisions';
import { fetchRatifyRight } from '@/lib/priorityListRatification';
import { fetchPublishRight } from '@/lib/sessionPublishRight';
import { fetchApproveRight } from '@/lib/commands/approvePriorityList';

export interface ActRightState extends ActRight {
  loading: boolean;
}

const NOT_ASKED: ActRightState = {
  allowed: false,
  allowedProvisional: false,
  configured: false,
  heldBy: null,
  basis: null,
  line: null,
  loading: false,
};

function useActRights(
  fetcher: (id: string) => Promise<ActRight>,
  ids: readonly string[],
): { loading: boolean; rights: Record<string, ActRight> } {
  // A stable key, so a new array with the same ids does not refetch.
  const key = useMemo(() => [...new Set(ids.filter(Boolean))].sort().join(','), [ids]);
  const [state, setState] = useState<{ key: string; rights: Record<string, ActRight> }>({ key: '', rights: {} });

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const wanted = key.split(',');
    void Promise.all(wanted.map((id) => fetcher(id))).then((answers) => {
      if (cancelled) return;
      setState({ key, rights: Object.fromEntries(wanted.map((id, i) => [id, answers[i]])) });
    });
    return () => {
      cancelled = true;
    };
  }, [fetcher, key]);

  return { loading: key !== '' && state.key !== key, rights: state.key === key ? state.rights : {} };
}

function useOneActRight(fetcher: (id: string) => Promise<ActRight>, id: string | null | undefined): ActRightState {
  const ids = useMemo(() => (id ? [id] : []), [id]);
  const { loading, rights } = useActRights(fetcher, ids);
  if (!id) return NOT_ASKED;
  const right = rights[id];
  return right ? { ...right, loading: false } : { ...NOT_ASKED, loading };
}

/** Accept (allowed) and accept provisionally (allowedProvisional), for a proposal or loop id. */
export function useCatchballAcceptRight(proposalOrLoopId: string | null | undefined): ActRightState {
  return useOneActRight(fetchAcceptRight, proposalOrLoopId);
}

/** Ratify, for one Priority List. */
export function usePriorityListRatifyRight(prioritySetId: string | null | undefined): ActRightState {
  return useOneActRight(fetchRatifyRight, prioritySetId);
}

/** Publish results, for one session (1001). */
export function useSessionPublishRight(sessionId: string | null | undefined): ActRightState {
  return useOneActRight(fetchPublishRight, sessionId);
}

/** `cycle:<id>` or `workspace:<id>`: the one key useOneActRight caches an answer under. */
function fetchApproveRightForScope(scope: string) {
  const [kind, id] = scope.split(':');
  return kind === 'cycle' ? fetchApproveRight(id) : fetchApproveRight(null, null, id);
}

/**
 * Approve items onto the Priority List, for the list's cycle (1001), or for its
 * workspace when the list has no cycle (1017).
 */
export function usePriorityListApproveRight(
  cycleId: string | null | undefined,
  workspaceId?: string | null,
): ActRightState {
  const scope = cycleId ? `cycle:${cycleId}` : workspaceId ? `workspace:${workspaceId}` : null;
  return useOneActRight(fetchApproveRightForScope, scope);
}

/** Several answers at once: the first refusal of `field`, else every one allowed. */
function useAllActRights(
  fetcher: (id: string) => Promise<ActRight>,
  ids: readonly string[],
  field: 'allowed' | 'allowedProvisional',
): ActRightState {
  const { loading, rights } = useActRights(fetcher, ids);
  if (ids.length === 0) return NOT_ASKED;
  if (loading) return { ...NOT_ASKED, loading: true };
  const answers = ids.map((id) => rights[id]).filter((r): r is ActRight => Boolean(r));
  const refused = answers.find((r) => !r[field]);
  if (refused) return { ...refused, loading: false };
  return {
    allowed: answers.every((r) => r.allowed),
    allowedProvisional: answers.every((r) => r.allowedProvisional),
    configured: answers.some((r) => r.configured),
    heldBy: answers.find((r) => r.heldBy)?.heldBy ?? null,
    basis: answers[0]?.basis ?? null,
    line: null,
    loading: false,
  };
}

/**
 * Ratify, for several Priority Lists ratified together (the Ratification page
 * ratifies every proposed list of a cycle at once): allowed only when every
 * one is, and the first refusal's line otherwise.
 */
export function usePriorityListRatifyRights(prioritySetIds: readonly string[]): ActRightState {
  return useAllActRights(fetchRatifyRight, prioritySetIds, 'allowed');
}

/**
 * Accept provisionally, for several proposals accepted together (Executive
 * Reconciliation accepts every waiting proposal of a cycle at once):
 * `allowedProvisional` only when every one is, and the first refusal's line
 * otherwise.
 */
export function useCatchballProvisionalAcceptRights(proposalIds: readonly string[]): ActRightState {
  return useAllActRights(fetchAcceptRight, proposalIds, 'allowedProvisional');
}
